import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import RejectedJsasPanel from '../../components/RejectedJsasPanel';
import { invalidateSafety } from '../../lib/safety-invalidation';
import { daysUntilCalendarDate, formatTimeAgo, parseCalendarDate, relativeCalendarDay, todayCalendarDate } from '../../lib/calendar-date';
import { feedTime, orderSafetyFeed, SafetyFeedEntry } from '../../lib/safety-feed';
import { readIncidentSeverity } from '../../lib/incident-types';
import Header from '../../components/Header';
import DatePicker from '../../components/DatePicker';
import {
  ArrowLeft,
  Shield,
  Clock,
  AlertTriangle,
  CheckCircle,
  ClipboardCheck,
  ClipboardList,
  Wrench,
  FileText,
  Loader2,
  X,
  Plus,
  BookOpen,
  History,
  FilePen,
  FolderOpen,
} from 'lucide-react';
import { useHideAgentFab } from '../../components/AgentFab';
import { CERT_TYPE_LABEL_MAP as CERT_TYPE_LABELS } from '../../lib/certTypes';
import { useBack } from '../../hooks/useBack';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { useAuth } from '../../contexts/AuthContext';
import { useTenantFormTiles } from '../../hooks/useTenantFormTiles';
import { useMyDrafts } from '../../hooks/useMyDrafts';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { queryFailureError } from '../../lib/load-failure';

// ─── Near Miss Report Modal ──────────────────────────────────────────────────
function NearMissModal({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => void }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState('');
  // Seeded with the TENANT's today (companies.time_zone), not the phone's —
  // after 18:00 in Alberta the device's UTC-derived day was already tomorrow.
  // `?? ''` only seeds the picker; with no zone there is no honest today.
  const timeZone = useTenantTimeZone();
  const [date, setDate] = useState(todayCalendarDate(timeZone) ?? '');
  const [severity, setSeverity] = useState('low');
  const [immediateAction, setImmediateAction] = useState('');
  const [preventiveAction, setPreventiveAction] = useState('');

  const mutation = useMutation({
    mutationFn: (data: any) => api.safety.createNearMiss(data),
    onSuccess: () => { onSuccess(); onClose(); },
  });

  /* The nav clearance rides on the card as a margin, not on the scroller as padding:
   * WebKit leaves a scroll container's bottom padding out of the scrollable overflow
   * region, so pb- here scrolled away and the Submit button stayed under the nav.
   * A flex item's bottom margin is honoured. */
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4 pt-16 overflow-y-auto" onClick={onClose}>
      <div className="bg-fieldiq-card rounded-xl p-5 w-full max-w-lg space-y-4 mb-[var(--bottom-chrome)]" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-white text-lg">Report Near Miss</h3>
          <button onClick={onClose} className="text-fieldiq-muted"><X size={18} /></button>
        </div>

        <div>
          <label className="text-xs text-fieldiq-text mb-1 block">Title *</label>
          <input value={title} onChange={e => setTitle(e.target.value)}
            className="w-full bg-fieldiq-dark rounded-lg px-3 py-2.5 text-sm border border-fieldiq-border focus:border-fieldiq-accent outline-none text-white"
            placeholder="Brief description of near miss" />
        </div>

        <div>
          <label className="text-xs text-fieldiq-text mb-1 block">Description *</label>
          <textarea value={description} onChange={e => setDescription(e.target.value)} rows={3}
            className="w-full bg-fieldiq-dark rounded-lg px-3 py-2.5 text-sm border border-fieldiq-border focus:border-fieldiq-accent outline-none text-white resize-none"
            placeholder="What happened? What could have happened?" />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-fieldiq-text mb-1 block">Date</label>
            <DatePicker value={date} onChange={setDate} />
          </div>
          <div>
            <label className="text-xs text-fieldiq-text mb-1 block">Severity</label>
            <select value={severity} onChange={e => setSeverity(e.target.value)}
              className="w-full bg-fieldiq-dark rounded-lg px-3 py-2.5 text-sm border border-fieldiq-border focus:border-fieldiq-accent outline-none text-white">
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
          </div>
        </div>

        <div>
          <label className="text-xs text-fieldiq-text mb-1 block">Location</label>
          <input value={location} onChange={e => setLocation(e.target.value)}
            className="w-full bg-fieldiq-dark rounded-lg px-3 py-2.5 text-sm border border-fieldiq-border focus:border-fieldiq-accent outline-none text-white"
            placeholder="Where did this occur?" />
        </div>

        <div>
          <label className="text-xs text-fieldiq-text mb-1 block">Immediate Action Taken</label>
          <textarea value={immediateAction} onChange={e => setImmediateAction(e.target.value)} rows={2}
            className="w-full bg-fieldiq-dark rounded-lg px-3 py-2.5 text-sm border border-fieldiq-border focus:border-fieldiq-accent outline-none text-white resize-none"
            placeholder="What was done right away?" />
        </div>

        <div>
          <label className="text-xs text-fieldiq-text mb-1 block">Preventive Action Recommended</label>
          <textarea value={preventiveAction} onChange={e => setPreventiveAction(e.target.value)} rows={2}
            className="w-full bg-fieldiq-dark rounded-lg px-3 py-2.5 text-sm border border-fieldiq-border focus:border-fieldiq-accent outline-none text-white resize-none"
            placeholder="What should be done to prevent recurrence?" />
        </div>

        {mutation.isError && (
          <p className="text-red-400 text-xs">{(mutation.error as any)?.response?.data?.error || 'Failed to submit'}</p>
        )}

        <button
          onClick={() => mutation.mutate({ title, description, location, date, severity, immediateAction, preventiveAction })}
          disabled={!title || !description || mutation.isPending}
          className="w-full bg-fieldiq-accent text-fieldiq-dark font-semibold rounded-lg py-2.5 text-sm disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {mutation.isPending && <Loader2 size={16} className="animate-spin" />}
          Submit Near Miss Report
        </button>
      </div>
    </div>
  );
}

export default function FieldSafetyScreen() {
  useHideAgentFab();  // Near Miss modal's Submit sits where the FAB floats.
  const navigate = useNavigate();
  const goBack = useBack('/field');
  const queryClient = useQueryClient();
  const timeZone = useTenantTimeZone();
  const { hasVertical } = useAuth();
  /* The tenant's own custom forms as tiles, after the built-in ones. The full
   * library ("More forms") and the drafts list are the field Forms screen of
   * whichever tree this hand is in: the trucking/rentals tree registers
   * /my-work/forms and the casing tree /field/forms, each with "My drafts" on
   * top (the same split App.tsx makes on these two verticals). */
  const formTiles = useTenantFormTiles();
  const draftsQuery = useMyDrafts();
  const draftCount = draftsQuery.data?.length;
  const formsListPath = hasVertical('trucking') || hasVertical('surface_rentals') ? '/my-work/forms' : '/field/forms';
  const [showNearMissModal, setShowNearMissModal] = useState(false);

  /* `?report=near-miss` opens the modal directly — the same contract
   * SafetyScreen honours, and deliberately identical to it. The incident form's
   * signpost sends a field worker here after Near Miss was removed from its type
   * list, so the link has to land on the OPEN form; arriving on the screen with
   * the modal shut leaves the reader hunting for the thing the link named.
   *
   * The param is consumed on arrival so a refresh, or a Back onto this entry,
   * does not reopen a modal the user already dismissed.
   *
   * Kept in step with the same effect in SafetyScreen. Two screens, one URL
   * contract: `nearMissReportPath` in lib/safety-routes.ts picks which of them a
   * given persona reaches, and it is the reason this one existed unwired — the
   * field trees never received the link until that repoint landed. */
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get('report') !== 'near-miss') return;
    setShowNearMissModal(true);
    const next = new URLSearchParams(searchParams);
    next.delete('report');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const { data: dashboard, isLoading, isError: dashboardFailed } = useQuery({
    queryKey: ['safety-dashboard'],
    queryFn: () => api.safety.dashboard().then(r => r.data.data ?? r.data ?? {}),
  });

  const { data: jsaData, error: jsaError } = useQuery({
    queryKey: ['safety-jsa-list'],
    queryFn: () => api.safety.jsaList().then(r => r.data.data ?? r.data ?? []),
  });

  const { data: meetingsData } = useQuery({
    queryKey: ['safety-meetings'],
    queryFn: () => api.safety.meetings().then(r => r.data.data ?? r.data ?? []),
  });

  const { data: incidentsData } = useQuery({
    queryKey: ['safety-incidents'],
    queryFn: () => api.safety.incidents().then(r => r.data.data ?? r.data ?? []),
  });

  const { data: myCerts } = useQuery({
    queryKey: ['my-certifications'],
    queryFn: () => api.employee.myCertifications().then(r => r.data.data ?? r.data ?? []),
  });

  const { data: nearMissData } = useQuery({
    queryKey: ['safety-near-misses'],
    queryFn: () => api.safety.nearMisses().then(r => r.data.data ?? r.data ?? []),
  });

  /* "SINCE LAST INCIDENT" HAS THREE STATES, as on the office /safety screen
   * (SafetyLayout): a number; an em dash with a sub-line naming which kind of
   * nothing it is — no incident ever is the best news on the page, and not the
   * same as an incident with no date; and a failed or pending read, an em dash
   * with no sub-line, because it knows nothing. It read "--" for all three. */
  const rawDaysSince = dashboard?.daysSinceLastIncident;
  const daysSince: number | null =
    typeof rawDaysSince === 'number' && Number.isFinite(rawDaysSince) ? rawDaysSince : null;
  const dashboardArrived = !isLoading && !dashboardFailed && dashboard !== undefined;
  const daysSinceSub = !dashboardArrived || daysSince !== null
    ? undefined
    : dashboard?.lastIncident
      ? 'Incident date not recorded'
      : 'No incident on record';
  const jsas = Array.isArray(jsaData) ? jsaData : [];
  const jsaPending = jsas.filter((j: any) => j.status === 'submitted' || j.status === 'draft').length;
  const meetings = Array.isArray(meetingsData) ? meetingsData : [];
  const incidents = Array.isArray(incidentsData) ? incidentsData : [];
  const certs: any[] = Array.isArray(myCerts) ? myCerts : [];
  const nearMisses: any[] = Array.isArray(nearMissData) ? nearMissData : [];

  const sevStyles: Record<string, string> = {
    low: 'bg-green-500/20 text-green-400',
    medium: 'bg-amber-500/20 text-amber-400',
    high: 'bg-orange-500/20 text-orange-400',
    critical: 'bg-red-500/20 text-red-400',
  };

  // Count expiring certs (within 90 days or expired)
  const now = new Date();
  const certsExpiring = certs.filter((c: any) => {
    const daysLeft = daysUntilCalendarDate(c.expiryDate, timeZone, now);
    return daysLeft != null && daysLeft <= 90;
  }).length;

  // Recent activity
  const recentActivity: SafetyFeedEntry[] = [];

  /**
   * `createdAt` is an instant; `meetingDate` is a CALENDAR DAY. The `||` handed
   * whichever survived to formatTimeAgo, which reads instants — so a meeting
   * with no createdAt parsed as UTC midnight and a meeting held this morning
   * announced itself as ~16 hours ago, or yesterday. Split, and let the day-only
   * arm say what it actually knows.
   */
  meetings.slice(0, 3).forEach((m: any) => {
    const t = feedTime(m.createdAt, m.meetingDate, timeZone);
    recentActivity.push({
      type: 'success',
      text: `Pre-job meeting completed${m.jobNumber ? ` - ${m.jobNumber}` : ''}`,
      time: t.label,
      ts: t.ts,
      kind: 'routine',
    });
  });

  // Cert expiry warnings.
  //
  // UNCAPPED ON PURPOSE. Six or more expiring certs fill every slot of Recent
  // Activity and push the meetings and JSAs out of it until they are rectified
  // — an expiring ticket is the one thing here a hand can still act on, so it
  // dominates for exactly as long as it stays unresolved. Incidents are
  // unaffected: they hold the reserved rank in `orderSafetyFeed` and yield only
  // to a newer incident.
  //
  // So do not add a `.slice()` here, and do not give `orderSafetyFeed` a
  // per-kind budget. Full reasoning, and the operator's decision, in PR #478.
  certs.forEach((c: any) => {
    const daysLeft = daysUntilCalendarDate(c.expiryDate, timeZone, now);
    if (daysLeft == null) return;
    if (daysLeft > 0 && daysLeft <= 90) {
      // THIS ROW HAS NO EVENT TIME, AND USED TO INVENT ONE.
      //
      // It read `new Date(now.getTime() - 5 * 3600000)` — a warning about a
      // FUTURE deadline, stamped five hours into the past. That was wrong on
      // both counts the entry carries. The label said "5 hours ago" about
      // nothing that happened; and once the feed gained a sort (#476) the same
      // number became a live position, so a cert expiring tomorrow ranked below
      // any meeting logged in the last five hours — and every warning shared the
      // one fabricated instant, leaving their order to whatever the API listed
      // first. Picking a different number moves the arbitrariness, it does not
      // remove it.
      //
      // The honest answer is that a deadline is not an event: `upcoming` ranks
      // these as a group and orders them soonest-first, and `ts` is the expiry
      // the row's own text names. No age is claimed, so `time` is empty.
      recentActivity.push({
        type: 'warning',
        text: `Your ${CERT_TYPE_LABELS[c.certificationType] || c.certificationType} cert expires in ${daysLeft} days`,
        time: '',
        ts: parseCalendarDate(c.expiryDate)?.getTime() ?? 0,
        kind: 'upcoming',
      });
    } else if (daysLeft === 0) {
      // A cert expiring TODAY is valid through today. It used to fall into the
      // branch below and be announced as already expired.
      // Also a deadline, and the nearest one there is — so `upcoming`, where
      // soonest-first puts it above every other warning. `time` stays 'today':
      // that names the deadline, not an age, which is the distinction the
      // empty label above is drawing.
      recentActivity.push({
        type: 'danger',
        text: `Your ${CERT_TYPE_LABELS[c.certificationType] || c.certificationType} cert expires today`,
        time: 'today',
        ts: parseCalendarDate(c.expiryDate)?.getTime() ?? 0,
        kind: 'upcoming',
      });
    } else if (daysLeft < 0) {
      // ONLY a negative count is a lapse. This was a bare `else`, so a cert
      // more than 90 days out — the first branch's ceiling — fell through to
      // here and read "Your Driver's Licence cert has expired · in 241 days"
      // (fe#815, Tyler Brennan). A cert that far out has nothing to say on
      // this feed and gets no row.
      //
      // SPLIT out of formatTimeAgo rather than converted: that
      // helper renders instants at its six other call sites (createdAt,
      // updatedAt, created_at) and `new Date()` is right for those. It was
      // wrong only here, where it was handed `expiryDate` — a calendar day.
      // It parsed to UTC midnight (18:00 the previous day in Alberta) and then
      // reported an HOURS-precision figure about a value that has no time of
      // day at all, so a cert expiring today read "has expired · 16 hours ago".
      // daysLeft is a span of calendar days counted from the tenant's today.
      // DELIBERATELY STILL `routine`. A lapsed cert is a past event: it has a
      // real instant, a true age, and no claim on the reserved band above.
      // Pinning it up there with the warnings was tried and is what the
      // "leaves an already-expired cert dated and ranked as the past event it
      // is" test exists to refuse.
      recentActivity.push({
        type: 'danger',
        text: `Your ${CERT_TYPE_LABELS[c.certificationType] || c.certificationType} cert has expired`,
        // Was a hand-rolled 'yesterday' / 'N days ago' here. Same strings, one
        // implementation — six other sites needed it, so it moved to the lib
        // rather than becoming a seventh copy.
        time: relativeCalendarDay(c.expiryDate, timeZone, now) ?? '',
        ts: parseCalendarDate(c.expiryDate)?.getTime() ?? 0,
        kind: 'routine',
      });
    }
  });

  jsas.slice(0, 2).forEach((j: any) => {
    if (j.status === 'approved') {
      const t = feedTime(j.updatedAt || j.createdAt, null, timeZone);
      recentActivity.push({ type: 'success', text: `JSA approved${j.jobNumber ? ` - ${j.jobNumber}` : ''}`, time: t.label, ts: t.ts, kind: 'routine' });
    } else if (j.status === 'submitted') {
      const t = feedTime(j.createdAt, null, timeZone);
      recentActivity.push({ type: 'warning', text: `JSA awaiting approval${j.jobNumber ? ` - ${j.jobNumber}` : ''}`, time: t.label, ts: t.ts, kind: 'routine' });
    }
  });

  /**
   * A SAFETY INCIDENT IS NOT AN EQUIPMENT INSPECTION, AND IT IS NEVER A SUCCESS.
   *
   * This block read:
   *
   *   type: 'success',
   *   text: `Equipment inspection passed${` - ${inc.description.slice(0, 40)}`}`
   *
   * `incidents` is `GET /safety/incidents` — `safety_incidents` rows: injuries,
   * environmental, property damage, motor vehicle. It has nothing to do with
   * equipment inspections, which live in `equipment_inspection_submissions` on
   * a different endpoint this screen does not even fetch. So the line named the
   * wrong record type, and then appended the first 40 characters of the injury
   * description to it — rendering, for a real minor-injury row, a GREEN line
   * with a CHECKMARK reading "Equipment inspection passed - cut his hand …".
   *
   * `type` is not cosmetic here: the renderer below maps 'success' to
   * text-green-400 + <CheckCircle>, and everything else to <AlertTriangle>. So
   * the tone had to move with the words. This is the #421 shape — a wrong thing
   * rendered as a confident, deliberate-looking success — and the reason the
   * test that covers it asserts the ICON as well as the text.
   *
   * Both the label and the tone are copied from the office sibling
   * (`SafetyScreen.tsx`), which has always had this right:
   *
   *   type: 'danger', text: `Incident reported${' - ' + inc.incidentNumber}`
   *
   * Deliberately IDENTICAL to it rather than improved. The two screens render
   * the same feed from the same endpoint, and one of them silently diverging is
   * exactly what produced this bug. `incidentNumber` is NOT NULL, so it always
   * renders; the description is dropped rather than shortened, because a
   * one-line injury detail on a shared field device is not this feed's job.
   * Adding the type label (`getIncidentTypeLabel`) would be a real improvement
   * and belongs in a change that lands it on BOTH screens.
   */
  incidents.slice(0, 2).forEach((inc: any) => {
    // `incidentDate` is a calendar day — see the meetings note above.
    const t = feedTime(inc.createdAt, inc.incidentDate, timeZone);
    recentActivity.push({
      type: 'danger',
      text: `Incident reported${inc.incidentNumber ? ` - ${inc.incidentNumber}` : ''}`,
      time: t.label,
      ts: t.ts,
      kind: 'incident',
    });
  });

  /**
   * ORDER, THEN CUT. This read `recentActivity.slice(0, 5)` with no sort at
   * all, over an array built in push order: meetings, then an UNBOUNDED
   * `certs.forEach`, then JSAs, then incidents LAST. Three meetings and two
   * expiring certs filled the cut, and the incident rows — appended after both
   * — never rendered. A hand looked at a safety feed with no sign an incident
   * existed.
   *
   * `orderSafetyFeed` reserves incidents ahead of routine items rather than
   * sorting on recency alone, because recency alone does not fix this: five
   * newer meetings displace an older incident just as completely. See the
   * function's own note.
   */
  const displayActivity = orderSafetyFeed(recentActivity, 5);

  if (isLoading) {
    return (
      <div className="animate-fade-in min-h-full bg-fieldiq-dark pb-[var(--bottom-chrome)]">
        <div className="max-w-[1920px] mx-auto"><Header /></div>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 text-fieldiq-accent animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in min-h-full bg-fieldiq-dark pb-[var(--bottom-chrome)]">
      <div className="max-w-[1920px] mx-auto">
        <Header />
        <div className="px-4 py-3 border-b border-fieldiq-border flex items-center gap-3">
          <button onClick={goBack} aria-label="Back" className="text-fieldiq-text">
            <ArrowLeft size={26} />
          </button>
          <h1 className="text-xl font-bold text-white">Safety</h1>
          {daysSince !== null && (
            <p className="text-xs text-fieldiq-muted mt-0.5">{daysSince} days without incident</p>
          )}
        </div>
      </div>

      <div className="max-w-[1920px] mx-auto px-4 py-5 space-y-6">
        {/* ── Stat Cards ── */}
        <div className="grid grid-cols-3 gap-3">
          <StatCard
            icon={<Shield size={20} className="text-green-400" />}
            iconBg="bg-green-500/15"
            value={daysSince ?? '\u2014'}
            valueColor="text-green-400"
            line1="Since Last"
            line2="Incident"
            sub={daysSinceSub}
          />
          <StatCard
            icon={<Clock size={20} className="text-amber-400" />}
            iconBg="bg-amber-500/15"
            value={jsaPending}
            valueColor="text-amber-400"
            line1="JSAs"
            line2="Awaiting"
          />
          <StatCard
            icon={<AlertTriangle size={20} className="text-red-400" />}
            iconBg="bg-red-500/15"
            value={certsExpiring}
            valueColor="text-red-400"
            line1="My Certs"
            line2="Expiring"
          />
        </div>

        {/* The reader's own rejected JSAs, with the reason — see RejectedJsasPanel. */}
        <RejectedJsasPanel jsas={Array.isArray(jsaData) ? jsaData : undefined} error={jsaError} />

        {/* ── Quick Actions (2x2 grid) ── */}
        <div>
          <h3 className="text-sm font-bold text-white mb-3">Quick Actions</h3>
          <div className="grid grid-cols-2 gap-3">
            <ActionCard
              icon={<AlertTriangle size={22} className="text-red-400" />}
              iconBg="bg-red-500/15"
              label="Report Incident"
              onClick={() => navigate('/field/safety/incident')}
            />
            <ActionCard
              icon={<ClipboardList size={22} className="text-amber-400" />}
              iconBg="bg-amber-500/15"
              label="JSA"
              onClick={() => navigate('/field/safety/jsa')}
            />
            {/* Casing only. /field/safety/meeting is a redirect the casing
                field tree registers and the trucking/rentals tree does not, so
                there the tap fell through to /my-work/today. That tree does
                register the form itself (/forms/pre-job-meeting); pointing a
                tile at it is a product decision left open (inspections PR 2).

                Pressure testing too: a PT hand lands in the casing field tree,
                which registers the redirect, and the form lists their PT jobs
                (GET /field/my-work). Two positive guards, never a negative. */}
            {(hasVertical('casing_running') || hasVertical('pressure_testing')) && (
              <ActionCard
                icon={<ClipboardCheck size={22} className="text-green-400" />}
                iconBg="bg-green-500/15"
                label="Pre-Job Meeting"
                onClick={() => navigate('/field/safety/meeting')}
              />
            )}
            {/* Casing only: the form reads and writes /casing/inspection-
                submissions, which answers a tenant without casing_running with
                a 403 — a PT tech tapped this and got a raw refusal on submit. */}
            {hasVertical('casing_running') && (
              <ActionCard
                icon={<Wrench size={22} className="text-blue-400" />}
                iconBg="bg-blue-500/15"
                label="Equipment Inspection"
                onClick={() => navigate('/field/safety/inspection')}
              />
            )}
            {/* The way back to work already filed. Beside the tile that files
                it, because "I completed an inspection and cannot see it
                afterwards" is a sentence about this pair of tiles.

                Casing only. The list reads /casing/inspection-submissions, and
                its route is registered in the casing field tree alone: in the
                trucking and rentals tree the tap fell through to the catch-all
                and landed on /my-work/today. */}
            {hasVertical('casing_running') && (
              <ActionCard
                icon={<History size={22} className="text-sky-400" />}
                iconBg="bg-sky-500/15"
                label="My Inspections"
                onClick={() => navigate('/field/safety/inspections')}
              />
            )}
            <ActionCard
              icon={<Shield size={22} className="text-orange-400" />}
              iconBg="bg-orange-500/15"
              label="Near Miss Report"
              onClick={() => setShowNearMissModal(true)}
            />
            <ActionCard
              icon={<BookOpen size={22} className="text-purple-400" />}
              iconBg="bg-purple-500/15"
              label="My Training"
              onClick={() => navigate('/safety/my-training')}
            />
            {formTiles.tiles.map(tile => (
              <ActionCard
                key={tile.id}
                icon={<FileText size={22} className="text-fieldiq-accent" />}
                iconBg="bg-fieldiq-accent/15"
                label={tile.name}
                onClick={() => navigate(tile.route)}
              />
            ))}
            {formTiles.hasMore && (
              <ActionCard
                icon={<FolderOpen size={22} className="text-fieldiq-accent" />}
                iconBg="bg-fieldiq-accent/15"
                label="More forms"
                onClick={() => navigate(formsListPath)}
              />
            )}
            {/* Only when there is one: an empty drafts list is not worth a tile. */}
            {draftCount !== undefined && draftCount > 0 && (
              <ActionCard
                icon={<FilePen size={22} className="text-fieldiq-text" />}
                iconBg="bg-fieldiq-border"
                label={`My drafts (${draftCount})`}
                onClick={() => navigate(formsListPath)}
              />
            )}
          </div>
          {/* A missing row of company forms is not "this company has none". */}
          {formTiles.failure && (
            <div className="mt-3">
              <LoadFailureNotice error={queryFailureError(formTiles.failureQuery as any)} kind={formTiles.failure} subject="your company's forms" />
            </div>
          )}
        </div>

        {/* ── My Certifications ── */}
        <div>
          <h3 className="text-sm font-bold text-white mb-3">My Certifications</h3>
          <div className="bg-fieldiq-card rounded-xl border border-fieldiq-border overflow-hidden">
            {certs.length === 0 ? (
              <div className="px-4 py-6 text-center text-fieldiq-muted text-sm">
                No certifications on file
              </div>
            ) : (
              <div className="divide-y divide-fieldiq-border">
                {certs.map((cert: any, i: number) => {
                  const label = CERT_TYPE_LABELS[cert.certificationType] || cert.certificationType || cert.name || 'Certification';
                  const daysLeft = daysUntilCalendarDate(cert.expiryDate, timeZone, now);

                  return (
                    <div key={cert.id || i} className="flex items-center justify-between px-4 py-3.5">
                      <div className="flex items-center gap-3">
                        <FileText size={16} className="text-fieldiq-muted" />
                        <span className="text-sm font-medium text-white">{label}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {daysLeft !== null && daysLeft <= 0 && (
                          <span className="text-xs font-medium text-red-400 bg-red-500/15 px-2.5 py-1 rounded-full">
                            Expired
                          </span>
                        )}
                        {daysLeft !== null && daysLeft > 0 && daysLeft <= 90 && (
                          <span className="text-xs font-medium text-red-400 bg-red-500/15 px-2.5 py-1 rounded-full">
                            Expires in {daysLeft} days
                          </span>
                        )}
                        <CheckCircle
                          size={18}
                          className={
                            daysLeft === null || daysLeft > 90
                              ? 'text-green-400'
                              : daysLeft > 0
                              ? 'text-amber-400'
                              : 'text-red-400'
                          }
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* ── Near Miss Reports ── */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold text-white flex items-center gap-2">
              <Shield size={14} className="text-orange-400" />
              Near Miss Reports
            </h3>
            <button
              onClick={() => setShowNearMissModal(true)}
              className="flex items-center gap-1 bg-fieldiq-accent text-fieldiq-dark text-xs font-semibold px-3 py-1.5 rounded-lg"
            >
              <Plus size={14} /> Report
            </button>
          </div>
          <div className="bg-fieldiq-card rounded-xl border border-fieldiq-border overflow-hidden">
            {nearMisses.length === 0 ? (
              <div className="px-4 py-6 text-center text-fieldiq-muted text-sm">No near miss reports yet</div>
            ) : (
              <div className="divide-y divide-fieldiq-border">
                {nearMisses.slice(0, 10).map((nm: any) => (
                  <div key={nm.id} className="px-4 py-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-white">{nm.title}</p>
                        <p className="text-xs text-fieldiq-muted mt-0.5 line-clamp-2">{nm.description}</p>
                        <div className="flex items-center gap-2 mt-1.5">
                          {/* GET /safety/near-misses is raw `SELECT si.*`, so it
                              answers the column's own name, `severity_level`.
                              This read bare `nm.severity`, which that relation
                              does not have — always undefined, so the pill has
                              never rendered once. The POST answers a drizzle row and
                              is camelCase, hence both. */}
                          {readIncidentSeverity(nm) && (
                            <span
                              data-testid="near-miss-severity"
                              className={`text-[10px] px-2 py-0.5 rounded-full font-semibold capitalize ${sevStyles[readIncidentSeverity(nm)!] || sevStyles.low}`}
                            >
                              {readIncidentSeverity(nm)}
                            </span>
                          )}
                          {nm.location && <span className="text-xs text-fieldiq-muted">{nm.location}</span>}
                          <span className="text-xs text-fieldiq-muted">{formatTimeAgo(nm.created_at || nm.createdAt, timeZone)}</span>
                        </div>
                      </div>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold capitalize ${nm.status === 'open' ? 'bg-amber-500/20 text-amber-400' : 'bg-green-500/20 text-green-400'}`}>
                        {nm.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── Recent Activity ── */}
        <div>
          <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
            <AlertTriangle size={14} className="text-amber-400" />
            Recent Activity
          </h3>
          <div className="bg-fieldiq-card rounded-xl border border-fieldiq-border overflow-hidden">
            {displayActivity.length === 0 ? (
              <div className="px-4 py-6 text-center text-fieldiq-muted text-sm">
                No recent safety activity
              </div>
            ) : (
              <div className="divide-y divide-fieldiq-border">
                {displayActivity.map((activity, index) => {
                  const color =
                    activity.type === 'success'
                      ? 'text-green-400'
                      : activity.type === 'warning'
                      ? 'text-amber-400'
                      : 'text-red-400';

                  return (
                    <div key={index} className="flex items-start gap-3 px-4 py-3.5">
                      <div className={`mt-0.5 ${color}`}>
                        {activity.type === 'success' ? (
                          <CheckCircle size={16} />
                        ) : (
                          <AlertTriangle size={16} />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        {/* Named so a test can read a row's label and its age
                            as two separate values without traversing the DOM to
                            find them. Both halves matter here: the pair IS the
                            ts/label invariant in lib/safety-feed. */}
                        <p data-testid="feed-row-text" className="text-sm text-white">{activity.text}</p>
                        <p data-testid="feed-row-time" className="text-xs text-fieldiq-muted mt-0.5">{activity.time}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {showNearMissModal && (
        <NearMissModal
          onClose={() => setShowNearMissModal(false)}
          onSuccess={() => invalidateSafety(queryClient)}
        />
      )}
    </div>
  );
}

/* ── Sub-components ── */

function StatCard({
  icon,
  iconBg,
  value,
  valueColor,
  line1,
  line2,
  sub,
}: {
  icon: React.ReactNode;
  iconBg: string;
  value: string | number;
  valueColor: string;
  line1: string;
  line2: string;
  /** Names which kind of absence an em dash is. Omitted, nothing renders. */
  sub?: string;
}) {
  return (
    <div className="bg-fieldiq-card rounded-xl border border-fieldiq-border p-4 text-center" data-testid="safety-stat-card">
      <div className={`w-10 h-10 rounded-full ${iconBg} flex items-center justify-center mx-auto mb-2`}>
        {icon}
      </div>
      <div className={`text-2xl font-bold ${valueColor}`}>{value}</div>
      <div className="text-xs text-fieldiq-muted mt-1 leading-tight">
        {line1}
        <br />
        {line2}
      </div>
      {sub && <div className="text-[11px] text-fieldiq-muted mt-1 leading-tight">{sub}</div>}
    </div>
  );
}

function ActionCard({
  icon,
  iconBg,
  label,
  onClick,
}: {
  icon: React.ReactNode;
  iconBg: string;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="bg-fieldiq-card rounded-xl border border-fieldiq-border p-5 text-center hover:border-fieldiq-accent/30 transition-colors"
    >
      <div className={`w-12 h-12 rounded-full ${iconBg} flex items-center justify-center mx-auto mb-3`}>
        {icon}
      </div>
      <span className="text-xs font-medium text-white">{label}</span>
    </button>
  );
}

